import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useEffectOnce } from 'react-use'
import { toast } from '@/hooks/use-toast'
import { queryClient } from '@/api/query-client'
import { queryKeys } from '@/api/query-keys'
import { clearApiAuthToken, getApiAuthToken, setApiAuthToken } from '@/components/auth/api-auth-token'
import { clearTableUiSessionStorage } from '@/lib/table-ui-session'
import { isMockBackend } from '@/lib/mock-backend'
import { mockUser } from '@/test/mock-data'
import { useAuthSession } from '@/components/auth/hooks/use-auth-session-mutation'
import { useAuthMe, type AuthUser } from '@/components/auth/hooks/use-auth-me-query'

type AuthContextType = {
  user: AuthUser | null
  isLoading: boolean
  login: () => void
  register: () => void
  logout: () => void
  getAccessToken: () => string | null
  setApiTokenFromLogin: (token: string) => void
}

const AuthContext = createContext<AuthContextType | null>(null)

function mockUserToAuthUser(): AuthUser {
  return {
    id: String(mockUser.id),
    username: mockUser.preferred_username ?? null,
    email: mockUser.email ?? null,
    roles: mockUser.roles ?? [],
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [mockUserState, setMockUserState] = useState<AuthUser | null>(null)
  const [apiToken, setApiTokenState] = useState<string | null>(() => getApiAuthToken())

  const setApiTokenFromLogin = useCallback((token: string) => {
    setApiAuthToken(token)
    setApiTokenState(token)
  }, [])

  const sessionMutation = useAuthSession({
    onError: (error: Error) => {
      const { title, description } = getAuthErrorMessage(error)
      toast({
        variant: 'destructive',
        title,
        description,
      })
    },
  })

  const {
    data: user,
    isLoading: isLoadingUser,
    isFetching: isFetchingUser,
    error: userError,
    status: userQueryStatus,
  } = useAuthMe({
    enabled: !isMockBackend,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: () => queryClient.getQueryData<AuthUser>(queryKeys.auth.me()),
  })

  useEffectOnce(() => {
    if (isMockBackend) return
    // Restore session if we have a stored token (v2 auth)
    if (getApiAuthToken()) {
      sessionMutation.mutate()
    }
  })

  useEffect(() => {
    if (userError) {
      queryClient.setQueryData(queryKeys.auth.me(), null)
      // Don't show "expired token" toast after logout (apiToken is already cleared)
      if (apiToken) {
        const { title, description } = getAuthErrorMessage(userError)
        toast({
          variant: 'destructive',
          title,
          description,
        })
      }
    }
  }, [userError, apiToken])

  const isWaitingForUser =
    (sessionMutation.isSuccess || apiToken) &&
    !user &&
    !userError &&
    (userQueryStatus === 'pending' || isFetchingUser || isLoadingUser)

  const isLoading = Boolean(
    (sessionMutation.isPending && !sessionMutation.isError) ||
    (isLoadingUser && !user) ||
    (isFetchingUser && !user) ||
    isWaitingForUser,
  )

  const getAccessTokenCb = useCallback(getApiAuthToken, [])
  const login = useCallback(() => {}, [])
  const register = useCallback(() => {}, [])

  const logout = useCallback(() => {
    clearApiAuthToken()
    setApiTokenState(null)
    clearTableUiSessionStorage()
    queryClient.cancelQueries()
    queryClient.setQueryData(queryKeys.auth.me(), null)
    queryClient.clear()
  }, [])

  const mockLogin = useCallback(() => {
    const u = mockUserToAuthUser()
    setMockUserState(u)
    queryClient.setQueryData(queryKeys.auth.me(), u)
  }, [])

  const mockLogout = useCallback(() => {
    clearApiAuthToken()
    setApiTokenState(null)
    clearTableUiSessionStorage()
    setMockUserState(null)
    queryClient.setQueryData(queryKeys.auth.me(), null)
    queryClient.clear()
  }, [])

  const value = useMemo(
    () =>
      isMockBackend
        ? {
            user: mockUserState,
            isLoading: false,
            login: mockLogin,
            register: mockLogin,
            logout: mockLogout,
            getAccessToken: () => 'mock-token',
            setApiTokenFromLogin: () => {},
          }
        : {
            user: user ?? null,
            isLoading,
            login,
            register,
            logout,
            getAccessToken: getAccessTokenCb,
            setApiTokenFromLogin,
          },
    [
      isMockBackend,
      mockUserState,
      mockLogin,
      mockLogout,
      user,
      isLoading,
      login,
      register,
      logout,
      getAccessTokenCb,
      setApiTokenFromLogin,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

function getAuthErrorMessage(error: unknown): { title: string; description: string } {
  const errorMessage =
    error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
      ? error.message
      : String(error)

  if (errorMessage.includes('401')) {
    return {
      title: 'Authentication Failed',
      description: 'Invalid or expired token. Please log in again.',
    }
  }
  if (errorMessage.includes('403')) {
    return {
      title: 'Access Denied',
      description: 'You do not have permission to access this resource.',
    }
  }
  if (errorMessage.includes('404')) {
    return {
      title: 'Service Not Found',
      description: 'The authentication service could not be reached. Please contact support.',
    }
  }
  if (
    errorMessage.includes('500') ||
    errorMessage.includes('502') ||
    errorMessage.includes('503')
  ) {
    return {
      title: 'Server Error',
      description: 'The authentication service is temporarily unavailable. Please try again later.',
    }
  }
  if (errorMessage.includes('NetworkError') || errorMessage.includes('Failed to fetch')) {
    return {
      title: 'Network Error',
      description:
        'Unable to connect to the authentication service. Please check your connection and ensure the backend API is running.',
    }
  }
  if (errorMessage.includes('CORS') || errorMessage.includes('Access-Control')) {
    return {
      title: 'Connection Error',
      description:
        'Unable to connect to the backend API. Please check that the API server is running and CORS is configured correctly.',
    }
  }
  if (errorMessage.includes('No token available')) {
    return {
      title: 'Authentication Required',
      description: 'No authentication token found. Please log in.',
    }
  }

  return {
    title: 'Authentication Failed',
    description: errorMessage || 'An unexpected error occurred. Please try again.',
  }
}
